/**
 * Eveloria Meta Pixel — ROAS-safe gap bridge
 *
 * WHY THIS IS NARROW:
 * WeTracked + Shopify Meta CAPI already own standard PDP/collection/checkout events.
 * The earlier sitewide bridge dual-fired fbq + Shopify.analytics.publish with
 * non-matching eventIDs, which duplicates AddToCart/ViewContent/IC and destabilizes
 * Meta learning / reported ROAS.
 *
 * THIS FILE ONLY:
 * - Fills the custom turmeric sales-LP AddToCart gap (fetch cart/add.js had no Meta signal)
 * - Fires ViewContent on turmeric sales LPs (page templates are not product pages)
 * - Does NOT re-init the pixel, does NOT publish Shopify analytics, does NOT
 *   emit Collection/Search/IC/AM sitewide
 */
(function () {
  if (window.__evMetaPixelInit) return;
  window.__evMetaPixelInit = true;

  var DEDUP_MS = 5000;
  var FBQ_WAIT_MS = 12000;
  var recent = {};
  var ctx = window.__evMeta || {};
  var isSalesLp = !!ctx.isSalesLp;

  function currencyCode() {
    return (
      ctx.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD'
    );
  }

  function once(key, fn) {
    var now = Date.now();
    if (recent[key] && now - recent[key] < DEDUP_MS) return false;
    recent[key] = now;
    fn();
    return true;
  }

  function eventId(prefix, parts) {
    return [prefix]
      .concat(parts || [])
      .concat([Date.now().toString(36), Math.random().toString(36).slice(2, 8)])
      .join('_');
  }

  function whenFbq(cb) {
    if (typeof window.fbq === 'function') {
      cb();
      return;
    }
    var started = Date.now();
    var timer = window.setInterval(function () {
      if (typeof window.fbq === 'function') {
        window.clearInterval(timer);
        cb();
        return;
      }
      if (Date.now() - started > FBQ_WAIT_MS) window.clearInterval(timer);
    }, 100);
  }

  function trackFbq(eventName, params, eid) {
    whenFbq(function () {
      try {
        if (eid) {
          window.fbq('track', eventName, params || {}, { eventID: eid });
        } else {
          window.fbq('track', eventName, params || {});
        }
      } catch (e) {}
    });
  }

  function dollarsFromCents(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return 0;
    return Math.round(n) / 100;
  }

  function normalizeLine(item) {
    if (!item || item.status === 'error' || item.message) return null;
    if (item.items && item.items.length) {
      return item.items.map(normalizeLine).filter(Boolean);
    }
    if (!item.variant_id && !item.id) return null;

    var qty = item.quantity || 1;
    var lineCents =
      item.final_line_price ||
      item.line_price ||
      (item.final_price != null ? item.final_price * qty : null) ||
      (item.price != null ? item.price * qty : 0);
    var unitCents =
      item.final_price != null
        ? item.final_price
        : item.price != null
          ? item.price
          : qty
            ? lineCents / qty
            : 0;

    return {
      variantId: String(item.variant_id || item.id || ''),
      name: item.product_title || item.title || (ctx.product && ctx.product.title) || '',
      quantity: qty,
      value: dollarsFromCents(lineCents),
      itemPrice: dollarsFromCents(unitCents)
    };
  }

  function trackAddToCart(raw) {
    // Sales LPs only — PDP/collection ATC stays with WeTracked / Shopify Meta.
    if (!isSalesLp) return;

    var normalized = normalizeLine(raw);
    if (!normalized) return;
    var lines = Array.isArray(normalized) ? normalized : [normalized];
    if (!lines.length) return;

    var key = lines
      .map(function (l) {
        return ['atc', l.variantId, l.quantity].join(':');
      })
      .join('|');

    once(key, function () {
      var contentIds = [];
      var contents = [];
      var value = 0;
      var name = '';
      var numItems = 0;

      lines.forEach(function (line) {
        if (!line.variantId) return;
        contentIds.push(line.variantId);
        contents.push({
          id: line.variantId,
          quantity: line.quantity,
          item_price: line.itemPrice
        });
        value += line.value;
        numItems += line.quantity;
        if (!name) name = line.name;
      });

      if (!contentIds.length) return;

      trackFbq(
        'AddToCart',
        {
          content_ids: contentIds,
          content_type: 'product',
          content_name: name,
          contents: contents,
          value: Math.round(value * 100) / 100,
          currency: currencyCode(),
          num_items: numItems
        },
        eventId('atc', [contentIds[0]])
      );
    });
  }

  function trackViewContent() {
    if (!isSalesLp) return;
    var product = ctx.product;
    if (!product || !(product.variantId || product.id)) return;

    var variantId = String(product.variantId || product.id);
    once('vc:' + variantId, function () {
      trackFbq(
        'ViewContent',
        {
          content_ids: [variantId],
          content_type: 'product',
          content_name: product.title || '',
          value: Number(product.price) || 0,
          currency: currencyCode()
        },
        eventId('vc', [variantId])
      );
    });
  }

  function isCartAddUrl(url) {
    return url && String(url).indexOf('/cart/add') !== -1;
  }

  function handleCartAddResponse(response) {
    if (!isSalesLp || !response || !response.ok) return;
    response
      .clone()
      .json()
      .then(trackAddToCart)
      .catch(function () {});
  }

  // Only patch network on sales LPs to avoid doubling WeTracked PDP ATC.
  if (isSalesLp && !window.__evMetaFetchPatched) {
    window.__evMetaFetchPatched = true;
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var req = args[0];
      var url = req && req.url ? req.url : req;
      return origFetch.apply(this, args).then(function (res) {
        if (isCartAddUrl(url)) handleCartAddResponse(res);
        return res;
      });
    };
  }

  if (isSalesLp && !window.__evMetaXhrPatched) {
    window.__evMetaXhrPatched = true;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__evMetaUrl = url;
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (isCartAddUrl(xhr.__evMetaUrl)) {
        xhr.addEventListener('load', function () {
          if (xhr.status < 200 || xhr.status >= 300) return;
          try {
            trackAddToCart(JSON.parse(xhr.responseText));
          } catch (e) {}
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  document.addEventListener('ev:cart-added', function (event) {
    if (event && event.detail) trackAddToCart(event.detail);
  });

  window.__evMetaTrackAddToCart = trackAddToCart;
  window.__evMetaTrackViewContent = trackViewContent;

  if (isSalesLp && (ctx.fireViewContent === 'always' || ctx.fireViewContent === true)) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', trackViewContent);
    } else {
      trackViewContent();
    }
  }
})();
